import type { Adsr, InstrumentSlot, SynthPreset } from "../types";
import { deepClone } from "./deepClone";

const silentAdsr = (): Adsr => ({ a: 0.01, d: 0.1, s: 0.5, r: 0.1 });

function osc(
  wave: SynthPreset["osc1"]["wave"],
  level: number,
  extras?: Partial<SynthPreset["osc1"]>,
): SynthPreset["osc1"] {
  return {
    wave,
    octave: 0,
    semis: 0,
    detuneCents: 0,
    level,
    ...extras,
  };
}

/** Display names for the first eight factory slots. */
export const DEFAULT_SLOT_NAMES = [
  "Bass",
  "Lead",
  "Kick",
  "Hat",
  "Pad",
  "Arp",
  "Snare",
  "Strings",
] as const;

/** Default instruments for slots 0..7 when unset. Extra slots fall back via defaultPresetForSlot. */
export const DEFAULT_PRESETS: readonly SynthPreset[] = [
  {
    name: "Bass",
    osc1: osc("saw", 0.7, { octave: -1 }),
    osc2: osc("square", 0.35, { octave: -1, detuneCents: -8, pwm: 0.45 }),
    filter: { type: "lowpass", cutoffHz: 420, resonance: 0.25 },
    ampEnv: { a: 0.005, d: 0.15, s: 0.7, r: 0.2 },
    modEnv: { a: 0.001, d: 0.2, s: 0, r: 0.05 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 1.5 }],
  },
  {
    name: "Lead",
    osc1: osc("saw", 0.55),
    osc2: osc("square", 0.4, { detuneCents: 7, pwm: 0.5 }),
    filter: { type: "lowpass", cutoffHz: 2400, resonance: 0.15 },
    ampEnv: { a: 0.01, d: 0.2, s: 0.55, r: 0.25 },
    modEnv: silentAdsr(),
    lfo: { rateHz: 5, wave: "sine", depth: 0.15 },
    routes: [{ source: "lfo", target: "pitch", depth: 0.15 }],
    glideMs: 20,
  },
  {
    name: "Kick",
    osc1: osc("sine", 1, { octave: -1 }),
    osc2: osc("noise", 0.12),
    filter: { type: "lowpass", cutoffHz: 800, resonance: 0.2 },
    ampEnv: { a: 0.001, d: 0.22, s: 0, r: 0.06 },
    modEnv: { a: 0.001, d: 0.08, s: 0, r: 0.01 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "pitch", depth: 24 },
      { source: "modEnv", target: "cutoff", depth: 2 },
    ],
  },
  {
    name: "Hat",
    osc1: osc("noise", 0.85),
    osc2: osc("square", 0.15, { octave: 2, pwm: 0.1 }),
    filter: { type: "highpass", cutoffHz: 6000, resonance: 0.1 },
    ampEnv: { a: 0.001, d: 0.06, s: 0, r: 0.04 },
    modEnv: { a: 0.001, d: 0.05, s: 0, r: 0.02 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 0.5 }],
  },
  {
    name: "Pad",
    osc1: osc("tri", 0.55, { octave: -1 }),
    osc2: osc("saw", 0.3, { octave: -1, detuneCents: 5 }),
    filter: { type: "lowpass", cutoffHz: 900, resonance: 0.2 },
    ampEnv: { a: 0.02, d: 0.25, s: 0.6, r: 0.35 },
    modEnv: { a: 0.01, d: 0.3, s: 0.2, r: 0.2 },
    lfo: { rateHz: 0.4, wave: "sine", depth: 0.2 },
    routes: [{ source: "lfo", target: "cutoff", depth: 0.8 }],
  },
  {
    name: "Arp",
    osc1: osc("square", 0.5, { pwm: 0.35 }),
    osc2: osc("square", 0.35, { octave: 1, detuneCents: -6, pwm: 0.5 }),
    filter: { type: "bandpass", cutoffHz: 1800, resonance: 0.35 },
    ampEnv: { a: 0.005, d: 0.12, s: 0.4, r: 0.15 },
    modEnv: silentAdsr(),
    lfo: { rateHz: 6, wave: "tri", depth: 0.25 },
    routes: [{ source: "lfo", target: "pwm", depth: 0.4 }],
    glideMs: 12,
  },
  {
    name: "Snare",
    osc1: osc("noise", 0.55),
    osc2: osc("tri", 0.25, { octave: 1 }),
    filter: { type: "highpass", cutoffHz: 2500, resonance: 0.15 },
    ampEnv: { a: 0.001, d: 0.1, s: 0, r: 0.05 },
    modEnv: { a: 0.001, d: 0.08, s: 0, r: 0.02 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 1.2 }],
  },
  {
    name: "Strings",
    osc1: osc("saw", 0.4),
    osc2: osc("saw", 0.4, { detuneCents: 12 }),
    filter: { type: "lowpass", cutoffHz: 3200, resonance: 0.1 },
    ampEnv: { a: 0.05, d: 0.4, s: 0.7, r: 0.6 },
    modEnv: { a: 0.1, d: 0.5, s: 0.4, r: 0.4 },
    lfo: { rateHz: 0.2, wave: "sine", depth: 0.3 },
    routes: [
      { source: "lfo", target: "pitch", depth: 0.1 },
      { source: "modEnv", target: "cutoff", depth: 1.5 },
    ],
    glideMs: 40,
  },
];

export function defaultPresetForSlot(slot: InstrumentSlot | number): SynthPreset {
  const index = Math.max(0, Math.floor(Number(slot)));
  const preset = DEFAULT_PRESETS[index] ?? DEFAULT_PRESETS[0]!;
  if (index < DEFAULT_PRESETS.length) {
    return clonePreset(preset);
  }
  const next = clonePreset(preset);
  next.name = defaultSlotName(index);
  return next;
}

export function defaultSlotName(slot: InstrumentSlot | number): string {
  return DEFAULT_SLOT_NAMES[slot] ?? `Inst ${Number(slot) + 1}`;
}

export function clonePreset(preset: SynthPreset): SynthPreset {
  return deepClone(preset);
}
